import { Injectable, inject } from '@angular/core';

import {
    ClaimTypeFullName,
    ClaimsService,
    CookieName,
    CookieService,
    DateTimeService,
    FrontendHelperService,
    HostApiService,
    Logger,
    NativeAppService,
    NavigationService,
    PostLoginService,
    RememberMeService,
    UserService,
    WINDOW,
    WebWorkerService,
    WindowEvent,
    WorkerType,
} from '@frontend/vanilla/core';
import { firstValueFrom } from 'rxjs';

import { GoogleAnalyticsConfig } from './google-analytics.client-config';
import { Ga4Event, MeasurementEvent, UserProperties } from './google-analytics.models';

/**
 * @desc
 * Service for extracting Google Analytics cookie data.
 *
 * @whatItDoes
 * This service reads the Google Analytics cookies and extracts the client ID and session ID,
 * storing them in separate cookies for easier access.
 *
 * @internal
 */
@Injectable({
    providedIn: 'root',
})
export class GoogleAnalyticsService {
    private readonly cookieService = inject(CookieService);
    private readonly dateTimeService = inject(DateTimeService);
    private readonly frontendHelperService = inject(FrontendHelperService);
    private readonly googleAnalyticsConfig = inject(GoogleAnalyticsConfig);
    private readonly hostApiService = inject(HostApiService);
    private readonly log = inject(Logger);
    private readonly nativeAppService = inject(NativeAppService);
    private readonly navigationService = inject(NavigationService);
    private readonly userService = inject(UserService);
    private readonly webWorkerService = inject(WebWorkerService);
    private readonly window = inject(WINDOW);
    private readonly rememberMeService = inject(RememberMeService);
    private readonly postLoginService = inject(PostLoginService);
    private readonly claimsService = inject(ClaimsService);

    private readonly ga4EventsAuth: Ga4Event[] = [];
    private readonly ga4EventsUnauth: Ga4Event[] = [];
    private readonly unauthMeasurement: MeasurementEvent = {
        client_id: this.cookieService.get(CookieName.VnGoogleAnalyticsClientId),
        user_id: undefined,
        timestamp_micros: this.getCustomTimeStamp,
        user_properties: this.commonUserProperties,
        events: [],
    };
    private static readonly MaxEventsPerRequest: number = 25;

    private get googleAnalyticsApiEndpoint(): string {
        return `${this.navigationService.location.baseUrl()}/${this.navigationService.location.culture}/api/googleanalytics/track`;
    }

    private get cookiesExpiryDate(): Date {
        return this.dateTimeService.getFutureDate(1);
    }

    constructor() {
        this.window.addEventListener(
            WindowEvent.BeforeUnload,
            () => {
                while (this.ga4EventsUnauth.length > 0) {
                    const events = this.ga4EventsUnauth.splice(0, GoogleAnalyticsService.MaxEventsPerRequest);
                    const blob = new Blob(
                        [
                            JSON.stringify({
                                ...this.getUnauthMeasurementWithCurrentTimestamp,
                                events,
                            }),
                        ],
                        {
                            type: 'application/json',
                        },
                    );

                    this.window.navigator.sendBeacon(this.googleAnalyticsApiEndpoint, blob);
                }
                while (this.ga4EventsAuth.length > 0) {
                    const events = this.ga4EventsAuth.splice(0, GoogleAnalyticsService.MaxEventsPerRequest);
                    const blob = new Blob([JSON.stringify({ ...this.measurement, events })], {
                        type: 'application/json',
                    });

                    this.window.navigator.sendBeacon(this.googleAnalyticsApiEndpoint, blob);
                }
            },
            false,
        );
    }

    // If user is in workflow it is considered authenticated, so we need to use below logic instead of just isAuthenticated.
    private get isAuthenticatedOrHasWorkflow(): boolean {
        const workflowType = this.claimsService.get(ClaimTypeFullName.WorkflowType);
        return !!workflowType;
    }

    private get measurement(): MeasurementEvent {
        return {
            client_id: this.cookieService.get(CookieName.VnGoogleAnalyticsClientId),
            user_id: this.userService.accountId ?? undefined,
            timestamp_micros: this.getCustomTimeStamp, // GA4 requires timestamp in microseconds
            user_properties: {
                ...this.commonUserProperties,
                ...this.authUserProperties,
            },
            events: [],
        };
    }

    private get commonUserProperties(): UserProperties {
        return {
            appinfo: {
                value: this.nativeAppService.isNative ? 'app traffic' : 'web traffic',
            },
            device_native: {
                value: this.nativeAppService.nativeMode.toLowerCase(),
            },
            page_fe: {
                value: this.frontendHelperService.getFrontendDescription(),
            },
            prof_country: {
                value: this.userService.country?.toLowerCase(),
            },
            prof_loggedin: {
                value: this.isAuthenticatedOrHasWorkflow ? 'logged_in' : 'not_logged_in',
            },
            remember_me: {
                value: this.rememberMeService.tokenExists() ? 'remember me cookie - enabled' : 'remember me cookie - disabled',
            },
        };
    }

    private get authUserProperties() {
        return {
            prof_customerid: {
                value: this.userService.accountId ?? undefined,
            },
            prof_master_account_id: {
                value: this.postLoginService.getMasterAccountId(),
            },
        };
    }

    private get isConsentGiven(): boolean {
        const consent = this.cookieService.get(CookieName.GoogleTrackingConsent);
        return !consent?.includes('C0002:0');
    }

    private get getCustomTimeStamp(): number {
        return this.dateTimeService.now().getTime() * 1000;
    }

    private get getUnauthMeasurementWithCurrentTimestamp(): MeasurementEvent {
        return {
            ...this.unauthMeasurement,
            timestamp_micros: this.getCustomTimeStamp,
        };
    }

    /**
     * Tracks a GA4 event by sending a measurement protocol request.
     *
     * This method constructs a MeasurementEvent object with the provided event name and parameters,
     * along with additional user and session information. It then sends this data to the specified
     * Google Analytics endpoint using the HostApiService.
     *
     * @param event The GA4 event to track, containing the event name and parameters.
     * @returns A promise that resolves when the tracking request is completed.
     */
    track(event: Ga4Event) {
        if (!this.isConsentGiven) {
            return;
        }

        const customTimestamp = this.getCustomTimeStamp.toString();
        const eventData = {
            name: event.name,
            params: {
                session_id: this.cookieService.get(CookieName.VnGoogleAnalyticsSessionId),
                page_hostname: this.navigationService.location.host(),
                page_name: this.navigationService.location.path(),
                page_trackerid: this.cookieService.get(CookieName.TrackerId),
                clean_page_URL: this.navigationService.location.baseUrl() + this.navigationService.location.path(),
                prof_loggedin_event: this.isAuthenticatedOrHasWorkflow
                    ? this.measurement.user_properties.prof_loggedin.value
                    : this.unauthMeasurement?.user_properties?.prof_loggedin?.value,
                custom_timestamp: customTimestamp,
                prof_customerid_event: this.userService.accountId || undefined,
                page_location: this.navigationService.location.absUrl(),
                page_referrer: this.navigationService.previousUrl,
                ...event.params,
            },
        };

        if (this.isAuthenticatedOrHasWorkflow) {
            this.ga4EventsAuth.push(eventData);
        } else {
            this.ga4EventsUnauth.push(eventData);
        }

        this.webWorkerService.createWorker(
            WorkerType.TrackEventsTimeout,
            { timeout: this.googleAnalyticsConfig.isConfigReady ? this.googleAnalyticsConfig.trackEventsTimeoutInMilliseconds : 5000 },
            async () => {
                while (this.ga4EventsUnauth.length > 0) {
                    const events = this.ga4EventsUnauth.splice(0, GoogleAnalyticsService.MaxEventsPerRequest);
                    await this.sendEvents(events, this.getUnauthMeasurementWithCurrentTimestamp, 'unauthenticated');
                }
                while (this.ga4EventsAuth.length > 0) {
                    const events = this.ga4EventsAuth.splice(0, GoogleAnalyticsService.MaxEventsPerRequest);
                    await this.sendEvents(events, this.measurement, 'authenticated');
                }

                this.webWorkerService.removeWorker(WorkerType.TrackEventsTimeout);
            },
        );
    }

    /**
     * @desc Updates the cookies for Google Analytics client ID and session ID.
     */
    updateCookies() {
        this.updateClientIdCookie();
        this.updateSessionIdCookie();
    }

    /**
     * Extracts the GA4 client ID from the `_ga` cookie.
     * Example: _ga=GA1.1.900335738.1755505472
     */
    private updateClientIdCookie() {
        const gaCookie = this.cookieService.get(CookieName.GoogleAnalytics);
        const gaClientId = gaCookie?.match(this.googleAnalyticsConfig.gaCookieClientIdRegex)?.[1];

        if (gaClientId) {
            this.cookieService.put(CookieName.VnGoogleAnalyticsClientId, gaClientId, { expires: this.cookiesExpiryDate });
        }
    }

    /**
     * Extracts session ID from the `_ga_<measurementId>` cookie.
     * Example: _ga_ABC123=GS2.1.s1754042772$o1$g1$t1754044274$j60$l0$h0
     */
    private updateSessionIdCookie() {
        const measurementId = this.googleAnalyticsConfig.measurementId.replace('G-', '');
        const gaSessionCookie = this.cookieService.get(`${CookieName.GoogleAnalytics}_${measurementId}`);
        const gaSessionId = gaSessionCookie?.match(this.googleAnalyticsConfig.gaCookieSessionIdRegex)?.[1];

        if (gaSessionId) {
            this.cookieService.put(CookieName.VnGoogleAnalyticsSessionId, gaSessionId, { expires: this.cookiesExpiryDate });
        }
    }

    private async sendEvents(events: Ga4Event[], measurement: MeasurementEvent, type: 'authenticated' | 'unauthenticated'): Promise<void> {
        try {
            await firstValueFrom(
                this.hostApiService.post(
                    'googleanalytics/track',
                    {
                        ...measurement,
                        events,
                    },
                    { keepalive: true },
                ),
            );
        } catch (error) {
            this.log.errorRemote(`Failed to track GA4 ${type} events: ${JSON.stringify(events)}`, error);
        }
    }
}
